import { DecisionDetail, DecisionListItem, NextBestDecision } from "../../models/home";
import { LanguageService } from "../language/language.service";
import { HomeDecisionActionPresentationRules } from "./home-decision-action-presentation.rules";
import {
  DecisionActionLike,
  DecisionActionPresentation,
  DecisionActionPrimaryMode,
  DecisionActionStatusTone,
  DecisionUxSubject
} from "./home-decision-action.types";
import { HomeDecisionActionCatalog } from "./home-decision-action-presentation.builder.catalog-selection";
import { HomeDecisionLegacyPresentationBuilder } from "./home-decision-action-presentation.builder.legacy-presentation";

export class HomeDecisionCatalogPresentationBuilder {
  constructor(
    private readonly lang: LanguageService,
    private readonly rules: HomeDecisionActionPresentationRules,
    private readonly catalog: HomeDecisionActionCatalog,
    private readonly legacy: HomeDecisionLegacyPresentationBuilder
  ) {}

  getHeroActionPresentation(nextDecision: NextBestDecision | null): DecisionActionPresentation {
    const decision = nextDecision?.decision;
    const subject = this.rules.toDecisionSubject(decision ?? null);
    return this.catalog.hasOperationalCatalog(decision, "hero", decision?.decisionId)
      ? this.buildCatalogActionPresentation(subject, this.catalog.getPrimaryActionForNextDecision(nextDecision), "hero")
      : this.legacy.buildLegacyActionPresentation(subject, nextDecision?.nextAction ?? null);
  }

  getDetailActionPresentation(detail: DecisionDetail | null): DecisionActionPresentation {
    const decision = detail?.decision;
    const subject = this.rules.toDecisionSubject(decision ?? null);
    return this.catalog.hasOperationalCatalog(decision, "detail", decision?.decisionId)
      ? this.buildCatalogActionPresentation(subject, this.catalog.getPrimaryAction(decision), "detail")
      : this.legacy.buildLegacyActionPresentation(subject, decision?.nextBestAction ?? null);
  }

  getListActionLabel(item: DecisionListItem): string | null {
    if (this.catalog.hasOperationalCatalog(item, "list", item.decisionId)) {
      return this.catalog.getPrimaryAction(item)?.label ?? this.lang.translate("home.decisionActionUx.noActionTitle");
    }
    const presentation = this.legacy.buildLegacyActionPresentation(
      this.rules.toDecisionSubject(item),
      item.nextBestAction ?? null
    );
    return presentation.primaryMode === "execute"
      ? item.nextBestAction?.label ?? presentation.summaryLabel
      : presentation.summaryLabel;
  }

  buildCatalogActionPresentation(
    decision: DecisionUxSubject | null,
    action: DecisionActionLike | null,
    surface: "hero" | "detail"
  ): DecisionActionPresentation {
    const urgency = this.rules.buildUrgencyPresentation(decision);
    if (!decision) return this.legacy.emptyPresentation(urgency);
    if (!action) return this.legacy.buildNoCatalogActionPresentation(decision.state, urgency, surface);
    const primaryMode = this.resolveCatalogPrimaryMode(action, surface);
    const disabledReason = this.getCatalogDisabledReason(action, primaryMode);
    return {
      action,
      primaryLabel: this.resolvePrimaryLabel(action, primaryMode),
      primaryMode,
      canExecute: primaryMode === "execute",
      disabledReason,
      summaryLabel: action.label || this.lang.translate("home.decisionActionUx.noActionTitle"),
      summaryCopy: disabledReason || this.rules.getActionDescription(action) || this.lang.translate("home.decisionActionUx.readyCopy"),
      statusTone: this.getCatalogStatusTone(action, primaryMode),
      urgencyLabel: urgency.label,
      urgencyTone: urgency.tone,
      showSecondaryDetail: surface === "hero" && primaryMode === "execute"
    };
  }

  getCatalogDisabledReason(action: DecisionActionLike, primaryMode: DecisionActionPrimaryMode): string | null {
    if (!action.enabled) return action.disabledReason || this.lang.translate("home.decisionDetail.actionDisabled");
    if (this.catalog.requiresMissingSchemaFallback(action)) return this.lang.translate("home.decisionDetail.actionNeedsForm");
    return primaryMode === "disabled"
      ? action.disabledReason || this.lang.translate("home.decisionDetail.actionDisabled")
      : null;
  }

  resolvePrimaryLabel(action: DecisionActionLike, mode: DecisionActionPrimaryMode): string | null {
    return mode === "none" ? null : action.label || this.lang.translate("home.decisionDetail.noActionAvailable");
  }

  getCatalogStatusTone(action: DecisionActionLike, mode: DecisionActionPrimaryMode): DecisionActionStatusTone {
    if (!action.enabled || this.catalog.requiresMissingSchemaFallback(action)) return "watch";
    if (mode === "execute") return "ready";
    return mode === "inspect" ? "watch" : "neutral";
  }

  resolveCatalogPrimaryMode(action: DecisionActionLike, surface: "hero" | "detail"): DecisionActionPrimaryMode {
    if (!action.enabled) return "disabled";
    if (surface === "hero") return this.catalog.canExecuteHeroAction(action) ? "execute" : "inspect";
    if (this.catalog.requiresMissingSchemaFallback(action)) return "disabled";
    return action.executionMode === "inspect" ? "inspect" : "execute";
  }
}
